<template>
  <div ref="accessor" style="all: initial">
    <div v-if="slide">
      <div class="thumbnail-item">
        <ThumbnailSlide v-if="showThumbnail" class="thumbnail" :slide="JSON.parse(slide)" :library="true" :size="160"/>
      </div>
    </div>
    <div
        v-else
        class="viewport-wrapper"
        :style="backgroundStyle"
    >
      <div
          class="viewport"
          ref="viewportRef"
          :style="{ transform: !slides[slideIndex]?.infiniteScroll ? `scale(${canvasScale})` : '', height: viewportHeight, width: viewportWidth }"
          @keydown="handleKeydown"
          @keyup="handleKeyup"
      >
        <div class="editButton" @click="openSlideEditor" v-if="inEditPresentation">
          <IconEdit size="24"/>
        </div>
        <div class="refreshButton" @click="refresh" v-if="inEditPresentation">
          <IconRefresh size="24"/>
        </div>
          <ViewerElement
              v-for="(element, elementIndex) in elementList.filter(element => !element.canvasHide)"
              :key="element.id"
              :elementInfo="element"
              :elementIndex="elementIndex + 1"
              v-show="!hiddenElementIdList.includes(element.id)"
          />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onBeforeMount, onMounted, onUnmounted, ref, watch} from 'vue'
import {storeToRefs} from 'pinia'
import {useCustomerStore, useGivingTreeStore, useMainStore, useSlidesStore} from '@/store'
import {PPTElement, SlideBackground} from '@/types/slides'
import appCss from '@/assets/styles/theme.css?inline'
import appDocumentCss from '@/assets/styles/document-theme.css?inline'
import ViewerElement from './ViewerElement.vue'
import useMicroservice from "@/hooks/useMicroservice";
import useSlideBackgroundStyle from "@/hooks/useSlideBackgroundStyle";
import ThumbnailSlide from "@/views/components/ThumbnailSlide/index.vue";
import useViewportSize from "@/views/Editor/Canvas/hooks/useViewportSize";
import {getValuePropositionAPI, ValuePropositionAPI} from "@/services/value-proposition-api";
import { debounce } from 'lodash'

import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";

const background = computed<SlideBackground | undefined>(() =>
    slideIndex.value >= 0 ? slides.value[slideIndex.value]?.background : currentSlide.value?.background)
const {backgroundStyle} = useSlideBackgroundStyle(background)
const {setVpSlideData, getUvpPiniaData, saveUvpPiniaData} = useMicroservice()
const givingTreeStore = useGivingTreeStore()
const showThumbnail = ref(false)
const customerStore = useCustomerStore()

const mainStore = useMainStore()
const {
  valueModelLoading,
  hiddenElementIdList,
  slideNanoid,
  calculatorId,
  textFormatPainter,
  valueModelLoaded,
  vpSlideId,
  editor,
  gateContent,
  vmId,
  canvasScale,
  recalculate
} = storeToRefs(mainStore)
const {currentSlide, slides, theme, author, created, updated, rules} = storeToRefs(useSlidesStore())
const slidesStore = useSlidesStore()
const canvasRef = ref<HTMLElement>()
const { viewportStyles } = useViewportSize(canvasRef)
const { chosenDropdownValue } = storeToRefs(useCustomerStore())

const viewportWrapperHeight = computed(() => 'auto')
const viewportHeight = computed(() => slides.value[slideIndex.value]?.infiniteScroll ? 'auto': viewportStyles.value.height + 'px')
const viewportWrapperScroll = computed(() => !slides.value[slideIndex.value]?.infiniteScroll ? 'hidden': 'visible')
const viewportWidth = computed(() => slides.value[slideIndex.value]?.infiniteScroll ? '' : '100%')

const viewportRef = ref<HTMLElement>()
const accessor = ref()
const inEditPresentation = ref(window['editLayout'] || false)
const props = defineProps({
  calcId: String,
  deckId: String,
  vmId: String,
  url: String,
  index: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: null
  },
  slide: {
    type: String,
    required: false,
  }
})

const slideIndexFromName = ref(-1)

const slideIndex = computed(() => {
  if (slideIndexFromName.value > -1) {
    return slideIndexFromName.value
  }

  if (props.index && props.index.trim()) {
    return parseInt(props.index.trim()) - 1
  }
  // keep in for calculator
  return 0
})


calculatorId.value = props.calcId ?? null
slideNanoid.value = (slideNanoid.value === '' && props['calc-id']) ? props['calc-id'] : slideNanoid.value

const urlParams = new URLSearchParams(window.location.search);
vpSlideId.value = urlParams.get('deck-id') || props.deckId || 'main';

vmId.value = props.vmId ?? ''

const handleKeydown = (event) => {
  event.stopPropagation();
}
const handleKeyup = (event) => {
  event.stopPropagation();
}


const {getActiveHookId, getComputedValue, getValueByHookId, getComputedTypeByHookId} = useComputedTreeValue()
const {getCalculatorUserInputs, setCalculatorUserInputs, emitUserDataForForm, setCalculatorUser } = useMicroservice()
const userInputs = ref([{}])
const userOutputs = ref({ outputs: {} })
const userId = ref('')
let intervalId;

onBeforeMount( async () => {
  if (calculatorId.value || vpSlideId) editor.value = false
  // we just want one slide to load the data and sync pinia with giving tree, not all of them
  if (!valueModelLoading.value) {
    valueModelLoading.value = true
    const {loadValueModel, initValueModelEventListener} = useMicroservice()
    if (calculatorId.value) {
      const userInputRequest = getCalculatorUserInputs(calculatorId.value)
      await loadValueModel('', '', calculatorId.value, false)

      const calcData = await userInputRequest
      userInputs.value = calcData
      if (calcData.outputs) userOutputs.value.outputs = calcData.outputs
      if (calcData.user) userId.value = calcData.user.id || ''

      setupUserData()

      initCalculatorEventListeners()

    } else {
      await loadValueModel('', '', '', false)
      initValueModelEventListener()
    }


  }
})

const insertIndexCss = (node: HTMLElement) => {
  try {
    if (document.getElementById('lpi-calc-loader')) {
      const scriptUrl = document.getElementById('lpi-calc-loader').src
      const cssUrl = scriptUrl.substring(0, scriptUrl.lastIndexOf('.js')) + '.css'

      const link = document.createElement('link');
      link.href = cssUrl
      link.rel = 'stylesheet';
      node.prepend(link)
    }
  } catch (e) {
    console.error(e)
  }
}

const copyInlineStyles = (node: HTMLElement) => {
  const headStyles = document.head.querySelectorAll("style")
  headStyles.forEach((styleElement) => {
    const clonedStyle = styleElement.cloneNode(true) as HTMLStyleElement
    node.prepend(clonedStyle)
  })
}

onMounted(() => {
  const style = document.createElement('style')
  style.innerHTML = appCss
  accessor.value.parentNode.appendChild(style)

  const documentStyle = document.createElement('style')
  documentStyle.innerHTML = appDocumentCss
  document.head.appendChild(documentStyle)

  copyInlineStyles(accessor.value.parentNode)
  insertIndexCss(accessor.value.parentNode)

  registerValuePropEventHandler()
  setLocalElementList()

  window.addEventListener("message", async (event) => {
    if (event.data.type === 'lpi-ungate-content') {
      gateContent.value = false
    }
  })


  if (window['Reveal']) {
     saveCompleteListener()

    const api: ValuePropositionAPI | null = getValuePropositionAPI()

    if (api?.getUVPId()) setupCustomerStore(api?.getUVPId())
  }

  setTimeout(() => {
    copyInlineStyles(accessor.value.parentNode)
  }, 1000)

})

const saveCompleteListener = async () => {
  document.addEventListener("save-complete", async function (event: any) {
    await saveUvpPiniaData('customer', event.detail.uvpId, customerStore.$state)
  })
}

import { throttle } from 'lodash'

const throttledSaveAndDispatch = throttle(async () => {
  const inputs = { inputs: userInputs.value.inputs }
  const userData = { ...inputs, ...userOutputs.value }

  emitUserDataForForm(userData, userId.value)
  await setCalculatorUserInputs(calculatorId.value, userData)

  if (!userId.value) {
    const calcData = await getCalculatorUserInputs(calculatorId.value)
    if (calcData.user) userId.value = calcData.user.id || ''
  }
}, 1000)

const saveAndDispatchUserData = async () => {
  return throttledSaveAndDispatch()
}


const modelUpdatedListener = () => {
  document.addEventListener('modelUpdatedEvent', async (event) => {
    if (event.detail.hookId) {
      const key = event.detail.hookId
      const type = getComputedTypeByHookId(key)
      const value = event.detail.updated

      userInputs.value.inputs[key] = value

      updateUserOutputs()

      await saveAndDispatchUserData()
    }
  })
}

const csvUpdateListener = () => {
  document.addEventListener('lpi-csv-update', async (event) => {
    if (event.detail.id) {
      const csvKey = event.detail.id
      const inputKey = `csv${csvKey}`
      userInputs.value.inputs[inputKey] = chosenDropdownValue.value[csvKey]

      updateUserOutputs()
    }

    // Save the mapped values too
    for (const value of event.detail.values) {
      // need to map percents as decimal
      userInputs.value.inputs[value.hookId] = value.isPercent() ? value.value / 100.0 : value.value
    }

    await saveAndDispatchUserData()
  })
}

const userFormSubmission = () => {
  window.addEventListener("message", async function (e) {
    if (e.data.type === "lpi-calculator-user-submit") {
      const data = {
        'first-name': e.data.detail.firstName,
        'last-name': e.data.detail.lastName,
        'email': e.data.detail.email
      }
      await setCalculatorUser(calculatorId.value, data)

      await saveAndDispatchUserData()
    }
  })
}

const setupCustomerStore = async (uvpId) => {
  const data = await getUvpPiniaData('customer', uvpId)
  if (data) customerStore.$state = data
}



const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
watch(valueModelLoaded, () => {
  setLocalElementList()
  showThumbnail.value = true
  theme.value.themeColors.map((c, i) => {
    document.documentElement.style.setProperty(`--theme-${i}`, c)
  })
  document.documentElement.style.setProperty(`--presentation-font`, fontName.value)
})

const openSlideEditor = () => {
  const params = new URLSearchParams(window.location.search)
  const preview = params.get('from') === 'vcp'

  const vmId = window['valuePropJson'].VPId
  const deckId = props.deckId ?? 'main'

  window.open(`${import.meta.env.VITE_APP_ENV_SERVER}${import.meta.env.VITE_APP_BASE_URL}editor?deckId=${deckId}&vmId=${vmId}&preview=${preview}`, '_blank');
}

const refresh = async () => {

  const parent = accessor.value.parentNode.host
  console.log('refreshing', parent)
  const container = parent.parentNode
  console.log('container', container)

  // get the new deck
  const {setUpSlide} = useMicroservice()
  await setUpSlide()

  const clonedElement = parent.cloneNode(false)
  container.insertBefore(clonedElement, parent)
  container.removeChild(parent)
}

const elementList = ref<PPTElement[]>([])
const setLocalElementList = () => {

  if (props.name) {
    const slide = slides.value.find(slide => slide.name === props.name)
    if (slide) {
      slideIndexFromName.value = slides.value.findIndex(slide => slide.name === props.name)

      const newList = JSON.parse(JSON.stringify(slide.elements));
      elementList.value = newList.filter(entry => entry.type !== 'dragdrop');
    }
  }
  else if (props.index) {
    const slideIndex = parseInt(props.index.trim()) - 1
    const newList = slides.value[slideIndex] ? JSON.parse(JSON.stringify(slides.value[slideIndex].elements)) : [];
    elementList.value = newList.filter(entry => entry.type !== 'dragdrop');
  } else {
    const newList = currentSlide.value ? JSON.parse(JSON.stringify(currentSlide.value.elements)) : [];
    elementList.value = newList.filter(entry => entry.type !== 'dragdrop');
  }
}


const checkWindowObjectExists = (ms: any, windowObject: any, resolution: any) => {
  if (window && window[windowObject]) {
    resolution()
  } else {
    setTimeout(() => checkWindowObjectExists(ms, windowObject, resolution), ms)
  }
}

const registerValuePropEventHandler = () => {
  if (window['ValuePropositionAPI']) {
    const changeEventHandler = {
      handleEvent: (event: any) => {
        if (props.deckId && event.action === 'edit_layout_data') {
          inEditPresentation.value = true
          window['editLayout'] = true
        }

        if (props.deckId && event.action == 'display_layout_data') {
          inEditPresentation.value = false
          window['editLayout'] = false
        }
      }
    }
    window['ValuePropositionAPI'].registerUIEvent('SLIDER_CHANGE', changeEventHandler)
  }
}

const setupUserData = () => {
  if (!userInputs.value.inputs) {
      userInputs.value.inputs = {}
  }

  const time = Date.now()
  let updated = false

  for (const hookId of Object.keys(userInputs.value.inputs)) {
    if (window['tree']['variablesByHookId'][hookId]) {
      window['tree']['variablesByHookId'][hookId].value = {
        // percents come in as decimal form on the value object
        value: userInputs.value.inputs[hookId],
        updateTree: false,
        time: time
      }
      updated = true
    }
    else if (hookId.indexOf("csv") === 0) {
      const csvKey = hookId.substring(3) // undo - `csv${csvKey}`
      chosenDropdownValue.value[csvKey] = userInputs.value.inputs[hookId]
    }
  }

  if (updated && window['tree']) {
    window['tree'].recalculate(time, [])
    recalculate.value = Date.now()
  }


  // Map the outputs
  const tiles = []

  slidesStore.slides.forEach((slide) => {
    const slidelayouts = slide.elements.filter((element) => element.type === 'slidelayout')
    slidelayouts.forEach((slidelayout) => {
      tiles.push(...extractTiles(slidelayout.layout))
    })
  })

  tiles.forEach(tile => {
    const { hookId, subType, type, id } = tile

    if (!hookId) return

    if (subType === 'out_text') {
      if (!userOutputs.value.outputs[hookId]) {
        userOutputs.value.outputs[hookId] = getValueByHookId(hookId)
      }
      return
    }
  })
}

const extractTiles = (layout) => {
  let widgets = [];

  function traverse(children) {
    children.forEach(child => {
      if (child.dragDropElement && child.dragDropElement.widgets) {
        child.dragDropElement.widgets.forEach(widgetGroup => {
          widgets = widgets.concat(widgetGroup.children);
        });
      }

      if (child.children && child.children.length > 0) {
        traverse(child.children);
      }
    });
  }

  if (layout && layout.children) {
    traverse(layout.children);
  }

  return widgets;
}

const initCalculatorEventListeners = () => {
  // Listening for updates to any input data changes
  modelUpdatedListener()

  // Listening for any csv dropdown data changes
  csvUpdateListener()

  // Listening for Form submission to get user data
  userFormSubmission()
}

const updateUserOutputs = () => {
  Object.entries(userOutputs.value.outputs).forEach(([key, output]) => {
    userOutputs.value.outputs[key] = getValueByHookId(key)
  })
}


// Clear the format painter state when the canvas is logged out
onUnmounted(() => {
  if (textFormatPainter.value) mainStore.setTextFormatPainter(null)
  clearInterval(intervalId);
})

</script>

<style lang="scss" scoped>
.editButton {
  z-index: 9999999;
  position: absolute;
  right: 50px;
  top: 10px;
  background-color: white;
}

.editButton:hover {
  cursor: pointer;
}

.refreshButton {
  z-index: 9999999;
  position: absolute;
  right: 10px;
  top: 10px;
  background-color: white;
}

.refreshButton:hover {
  cursor: pointer;
}

.viewport-wrapper {
  width: 100%;
  height: v-bind(viewportWrapperHeight);
  background-position: center;
  overflow: v-bind(viewportWrapperScroll);
}

:deep(.p-hidden-accessible) {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

</style>
